import * as React from 'react'

import { cn } from '../lib/utils'

/** The five stops, left to right. `Any` is the centre rest — no preference. */
export enum FrequencyStop {
  Never = 'never',
  Rarely = 'rarely',
  Any = 'any',
  Weekly = 'weekly',
  Often = 'often'
}

const STOPS = [FrequencyStop.Never, FrequencyStop.Rarely, FrequencyStop.Any, FrequencyStop.Weekly, FrequencyStop.Often]

// Literal class strings — Tailwind's content scan can't see interpolated names.
const SELECTED_BG: Record<FrequencyStop, string> = {
  [FrequencyStop.Never]: 'bg-amber-700',
  [FrequencyStop.Rarely]: 'bg-amber-400',
  [FrequencyStop.Any]: 'bg-gray-400',
  [FrequencyStop.Weekly]: 'bg-brand-green-500',
  [FrequencyStop.Often]: 'bg-brand-green-700'
}

export interface FrequencyDialProps {
  value: FrequencyStop
  onChange: (stop: FrequencyStop) => void
  /** Accessible name for the group, already translated — e.g. "Red meat, dinners a week". */
  label: string
  /** Names each stop, already translated — e.g. "Never — kept off the plan". */
  stopLabel: (stop: FrequencyStop) => string
  className?: string
}

/**
 * A five-stop, single-select dial for "how often" — Never, Rarely, Any, Weekly, Often.
 *
 * A stop is a position, not a magnitude: stops left of the selection don't fill. Sibling of
 * `DotScale` and `Rating` — same native-radio mechanics, so arrow keys and the single tab stop come
 * from the browser.
 */
export function FrequencyDial({ value, onChange, label, stopLabel, className }: FrequencyDialProps) {
  // Radios group by `name`, so each dial on a page needs its own.
  const groupName = React.useId()

  return (
    <span role="radiogroup" aria-label={label} className={cn('inline-flex items-center gap-1', className)}>
      {STOPS.map((stop) => {
        const selected = stop === value
        return (
          <label
            key={stop}
            // Not through `cn`: tailwind-merge reads `outline-[3px]` as clashing with `outline` and drops the style.
            className="group flex size-11 cursor-pointer items-center justify-center rounded-full has-[:focus-visible]:outline has-[:focus-visible]:outline-[3px] has-[:focus-visible]:-outline-offset-2 has-[:focus-visible]:outline-pelorous-500 dark:has-[:focus-visible]:outline-pelorous-300">
            <input type="radio" name={groupName} value={stop} checked={selected} onChange={() => onChange(stop)} className="sr-only" />
            <span className="sr-only">{stopLabel(stop)}</span>
            <span
              className={cn(
                'rounded-full transition-[transform,background-color,width,height] duration-150 motion-reduce:transition-none',
                selected
                  ? cn('size-[1.125rem] ring-[3px] ring-track ring-offset-2 ring-offset-card', SELECTED_BG[stop])
                  : cn(
                      'size-2.5 group-hover:scale-[1.35] motion-reduce:group-hover:scale-100',
                      // The centre stop rests hollow so "no preference" never reads as a setting.
                      stop === FrequencyStop.Any
                        ? 'bg-card ring-[1.5px] ring-inset ring-gray-300 dark:ring-gray-600'
                        : 'bg-gray-200 dark:bg-gray-700'
                    )
              )}
            />
          </label>
        )
      })}
    </span>
  )
}
